import React, { useEffect, useState, useRef } from 'react';
import { X, Upload, FileText, Users, Check, AlertCircle, Download } from 'lucide-react';
import { InviteUsersData } from '../types';

interface InviteUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (data: InviteUsersData) => Promise<void>;
  loading: boolean;
  // Optional: which tab to show when opening
  initialTab?: TabType;
}

type TabType = 'csv' | 'emails' | 'sourcing';

export const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  loading,
  initialTab,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('csv');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [emails, setEmails] = useState('');
  const [sourcingRequirement, setSourcingRequirement] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset/set tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab || 'csv');
      setError(null);
      setSuccess(false);
    }
  }, [isOpen, initialTab]);

  const tabs = [
    { id: 'csv' as TabType, label: 'Upload CSV', icon: Upload },
    { id: 'emails' as TabType, label: 'Add Emails', icon: FileText },
    { id: 'sourcing' as TabType, label: 'Source Users', icon: Users },
  ];

  const handleSubmit = async () => {
    try {
      setError(null);
      setSuccess(false);

      let inviteData: InviteUsersData = {};

      if (activeTab === 'csv') {
        if (!csvFile) {
          setError('Please select a CSV file');
          return;
        }
        inviteData.csv_file = csvFile;
      } else if (activeTab === 'emails') {
        if (!emails.trim()) {
          setError('Please enter at least one email address');
          return;
        }
        inviteData.emails = emails.trim();
      } else if (activeTab === 'sourcing') {
        if (!sourcingRequirement.trim()) {
          setError('Please describe your user requirements');
          return;
        }
        inviteData.sourcing_requirement = sourcingRequirement.trim();
      }

      await onInvite(inviteData);
      setSuccess(true);
      
      // Reset form
      setCsvFile(null);
      setEmails('');
      setSourcingRequirement('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Close modal after success for non-sourcing requests
      if (activeTab !== 'sourcing') {
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite users');
    }
  };

  const handleFileSelect = (file: File) => {
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }
    setCsvFile(file);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'email,name\nexample@company.com,John Doe\nuser@example.com,Jane Smith';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user-template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const validateEmails = (emailText: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const separators = /[,;\n]/;
    const emailList = emailText.split(separators).map(e => e.trim()).filter(e => e);
    
    const validEmails = emailList.filter(email => emailRegex.test(email));
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));
    
    return { validEmails, invalidEmails, totalEmails: emailList.length };
  };

  const emailValidation = validateEmails(emails);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden ring-1 ring-zinc-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="text-xl font-semibold text-zinc-900">Invite Users</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-zinc-200">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center px-4 py-3 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-zinc-900 border-b-2 border-zinc-900 bg-zinc-50'
                      : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <Check className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700">
                {activeTab === 'sourcing' 
                  ? 'Sourcing request submitted! Sourcing time: 10-24 hours.'
                  : 'Users invited successfully!'
                }
              </span>
            </div>
          )}

          {/* CSV Upload Tab */}
          {activeTab === 'csv' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Upload CSV File
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragOver
                      ? 'border-zinc-400 bg-zinc-50'
                      : 'border-zinc-300 hover:border-zinc-400'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                >
                  {csvFile ? (
                    <div className="flex items-center justify-center">
                      <FileText className="w-8 h-8 text-green-500 mr-3" />
                      <div>
                        <p className="font-medium text-slate-900">{csvFile.name}</p>
                        <p className="text-sm text-slate-500">
                          {(csvFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
                      <p className="text-zinc-600 mb-2">
                        Drag and drop your CSV file here, or{' '}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-zinc-900 hover:underline font-medium"
                        >
                          browse
                        </button>
                      </p>
                      <p className="text-xs text-zinc-500">
                        CSV file must include an "email" column
                      </p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileSelect(file);
                  }}
                  className="hidden"
                />
              </div>
              <div className="text-center">
                <button
                  onClick={downloadTemplate}
                  className="inline-flex items-center text-sm text-zinc-900 hover:underline"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Download CSV template
                </button>
              </div>
            </div>
          )}

          {/* Add Emails Tab */}
          {activeTab === 'emails' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  Email Addresses
                </label>
                <textarea
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  placeholder="jane@site.com, john@site.com&#10;Or one email per line"
                  className="w-full h-32 px-3 py-2 border border-zinc-300 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Separate emails with commas, semicolons, or line breaks
                </p>
              </div>
              {emails && (
                <div className="text-sm text-zinc-600">
                  {emailValidation.validEmails.length > 0 && (
                    <p className="text-green-600">
                      ✓ {emailValidation.validEmails.length} valid email(s)
                    </p>
                  )}
                  {emailValidation.invalidEmails.length > 0 && (
                    <p className="text-red-600">
                      ✗ {emailValidation.invalidEmails.length} invalid email(s)
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Source Users Tab */}
          {activeTab === 'sourcing' && (
            <div className="space-y-4">
              <div className="mb-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <div>
                  <p>
                    This is for enterprise customers. A member from Mangodesk will reach out to you shortly.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  User Requirements
                </label>
                <textarea
                  value={sourcingRequirement}
                  onChange={(e) => setSourcingRequirement(e.target.value)}
                  placeholder="English teachers in Canada with 10+ years of experience"
                  className="w-full h-32 px-3 py-2 border border-zinc-300 rounded-xl focus:ring-2 focus:ring-zinc-900 focus:border-transparent resize-none"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Describe exactly who you need
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-zinc-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full bg-white text-zinc-900 ring-1 ring-zinc-300 hover:bg-zinc-50 hover:ring-zinc-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || success}
            className="px-6 py-2 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            style={{ backgroundColor: 'var(--accent)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--accent)')}
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            )}
            {activeTab === 'sourcing' ? 'Submit Request' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};